import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiEmailOutline,
  mdiLink,
  mdiLinkOff,
  mdiDotsVertical,
} from "@mdi/js";
import { useApp } from "app/contexts/AppContext";
import PageLayout from "app/components/common/PageLayout";
import HowItWorksButton from "app/components/common/HowItWorksButton";
import InfoTooltip from "app/components/common/InfoTooltip";
import { PartialLoadingSpinner } from "app/components/LoadingSpinner";
import CustomToast from "app/components/common/CustomToast";
import {
  fetchProviders,
  fetchEmailTemplates,
  fetchSenders,
  changeSender,
  clearChangeSenderStatus,
} from "./slice";
import EmailHowItWorksModal from "./EmailHowItWorksModal";
import ChangeSenderModal from "./ChangeSenderModal";
import "./emailAutomation.css";

const navigateToConnection = (navigate) => {
  navigate("/user-info", {
    state: { scrollTo: "calendar-connection" },
  });
};

const buildTags = (template) => {
  const tags = [];
  if (template.trigger_display) {
    tags.push(template.trigger_display);
  }
  if (template.cc_agent) {
    tags.push("CC Agent");
  }
  return tags;
};

const CATEGORY_SECTIONS = [
  { key: "pre_inspection", label: "Pre-Inspection Email" },
  { key: "post_inspection", label: "Post-Inspection Email" },
  { key: "contract_payment", label: "Contract & Payment Email" },
];

const StatusBadge = ({ isActive }) => (
  <div className="email-card-status">
    <span className={`status-dot ${isActive ? "active" : "inactive"}`} />
    <span className="status-text">{isActive ? "Active" : "Inactive"}</span>
  </div>
);

const EmailTag = ({ label }) => <span className="email-tag">{label}</span>;

const EmailCard = ({ template, onEdit }) => {
  const tags = buildTags(template);
  return (
    <div className={`email-card ${!template.is_active ? "inactive" : ""}`}>
      <div className="email-card-text">
        <h5 className="email-card-title">{template.name}</h5>
        <p className="email-card-description">{template.description}</p>
      </div>
      <StatusBadge isActive={template.is_active} />
      <div className="email-card-tags">
        {tags.map((tag) => (
          <EmailTag key={tag} label={tag} />
        ))}
      </div>
      <Button
        variant="outline-primary"
        className="email-card-edit-btn"
        onClick={() => onEdit(template.id)}
      >
        Edit
      </Button>
    </div>
  );
};

const NotConnectedState = ({ onConnect }) => (
  <div className="not-connected-state">
    <h5 className="not-connected-title">Start Sending Emails Automatically</h5>
    <p className="not-connected-description">
      Connect your email account to send customized emails like confirmations,
      reminders, and reports automatically.
    </p>
    <Button
      variant="primary"
      className="connect-account-btn"
      onClick={onConnect}
    >
      Connect Account
    </Button>
  </div>
);

const SenderCard = ({
  sender,
  isDisconnected,
  onChangeSender,
  onManageConnection,
  onReconnect,
}) => (
  <div className="sender-card-wrapper">
    <div
      className={`sender-card ${isDisconnected ? "sender-card-disconnected" : ""}`}
    >
      <div className="sender-info">
        <Icon path={mdiEmailOutline} size={1.33} className="sender-icon" />
        <div className="sender-details">
          <div className="sender-name-row">
            <h5 className="sender-name">{sender.name}</h5>
            {isDisconnected ? (
              <Icon path={mdiLinkOff} size={1} className="sender-disconnected-icon" />
            ) : (
              <Icon path={mdiLink} size={1} className="sender-link-icon" />
            )}
          </div>
          <p className="sender-email">{sender.email}</p>
        </div>
      </div>
      <div className="sender-actions">
        {isDisconnected && (
          <Button
            variant="primary"
            size="sm"
            className="sender-reconnect-btn"
            onClick={onReconnect}
          >
            Re-connect
          </Button>
        )}
        <Dropdown align="end">
          <Dropdown.Toggle
            as="button"
            className="sender-more-btn"
            aria-label="More options"
          >
            <Icon path={mdiDotsVertical} size={0.67} />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={onChangeSender}>
              Change Sender
            </Dropdown.Item>
            <Dropdown.Item onClick={onManageConnection}>
              Manage Connection
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
    {isDisconnected && (
      <p className="sender-disconnected-warning">
        This sender account is disconnected. Emails cannot be sent until
        reconnected.
      </p>
    )}
  </div>
);

const EmailSection = ({ label, templates, onEditEmail }) => {
  if (templates.length === 0) {
    return null;
  }

  return (
    <div className="email-section">
      <p className="email-section-label">{label}</p>
      <div className="email-cards">
        {templates.map((template) => (
          <EmailCard
            key={template.id}
            template={template}
            onEdit={onEditEmail}
          />
        ))}
      </div>
    </div>
  );
};

const EmailAutomation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    device: { isMobile },
  } = useApp();
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showChangeSender, setShowChangeSender] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const {
    sender,
    isConnected,
    fetchStatus,
    templates,
    templatesFetchStatus,
    senders,
    senderProviderId,
    hasDisconnectedProvider,
    sendersFetchStatus,
    changeSenderStatus,
    changeSenderError,
  } = useSelector((state) => state.emailAutomation);

  useEffect(() => {
    if (fetchStatus === "idle") {
      dispatch(fetchProviders());
    }
  }, [dispatch, fetchStatus]);

  useEffect(() => {
    if (isConnected && templatesFetchStatus === "idle") {
      dispatch(fetchEmailTemplates());
    }
  }, [dispatch, isConnected, templatesFetchStatus]);

  useEffect(() => {
    if (isConnected && sendersFetchStatus === "idle") {
      dispatch(fetchSenders());
    }
  }, [dispatch, isConnected, sendersFetchStatus]);

  // Handle change sender success/error
  useEffect(() => {
    if (changeSenderStatus === "success") {
      setShowChangeSender(false);
      setToastMessage("Sender updated successfully.");
      setShowSuccessToast(true);
      dispatch(clearChangeSenderStatus());
    } else if (changeSenderStatus === "failed") {
      setToastMessage(changeSenderError || "Failed to change sender.");
      setShowErrorToast(true);
      dispatch(clearChangeSenderStatus());
    }
  }, [changeSenderStatus, changeSenderError, dispatch]);

  const templatesByCategory = useMemo(() => {
    const grouped = {};
    for (const section of CATEGORY_SECTIONS) {
      grouped[section.key] = templates.filter(
        (t) => t.category === section.key
      );
    }
    return grouped;
  }, [templates]);

  const handleConnectAccount = () => {
    navigateToConnection(navigate);
  };

  const handleEditEmail = (templateId) => {
    navigate(`/email-automation/${templateId}/edit`);
  };

  const handleChangeSenderConfirm = (providerId) => {
    dispatch(changeSender({ providerId }));
  };

  // Current preferred sender from the email-automation/sender API response
  const sendersArray = Array.isArray(senders) ? senders : [];
  const preferredSender =
    sendersArray.find((s) => s.id === senderProviderId) ||
    sendersArray.find((s) => s.is_preferred);
  const currentPreferredId = preferredSender?.id;

  // Display sender: prefer the one from sender API, fall back to scheduling provider
  const fallbackSender = !preferredSender && sendersArray.length > 0
    ? sendersArray.find((s) => s.provider_type !== "virtual") || sendersArray[0]
    : null;
  const activeSender = preferredSender || fallbackSender;
  const displaySender = activeSender
    ? { name: activeSender.name, email: activeSender.email }
    : sender;
  const isSenderDisconnected = activeSender
    ? !activeSender.connected
    : hasDisconnectedProvider;

  if (fetchStatus === "failed") {
    return (
      <PageLayout
        title="Email Automation"
        headerActions={
          <HowItWorksButton onClick={() => setShowHowItWorks(true)} />
        }
        className="email-automation-container"
      >
        <div className="error-state">
          <p>Failed to load email automation settings. Please try again.</p>
          <Button variant="primary" onClick={() => dispatch(fetchProviders())}>
            Retry
          </Button>
        </div>
      </PageLayout>
    );
  }

  const isLoading =
    fetchStatus === "loading" || templatesFetchStatus === "loading";

  return (
    <PageLayout
      title="Email Automation"
      headerActions={
        <HowItWorksButton onClick={() => setShowHowItWorks(true)} />
      }
      className="email-automation-container"
    >
      {isLoading ? (
        <PartialLoadingSpinner />
      ) : !isConnected ? (
        <NotConnectedState onConnect={handleConnectAccount} />
      ) : (
        <>
          {/* Sender Section */}
          <div className="sender-section">
            <div className="sender-label">
              <span>Sender</span>
              <InfoTooltip
                text="All automated emails will be sent from this address."
                placement="top"
                style={{ display: "inline-flex", alignItems: "center" }}
              />
            </div>
            <SenderCard
              sender={displaySender}
              isDisconnected={isSenderDisconnected}
              onChangeSender={() => setShowChangeSender(true)}
              onManageConnection={() => navigateToConnection(navigate)}
              onReconnect={() => navigateToConnection(navigate)}
            />
          </div>

          {/* Email Template Sections */}
          {CATEGORY_SECTIONS.map((section) => (
            <EmailSection
              key={section.key}
              label={section.label}
              templates={templatesByCategory[section.key]}
              onEditEmail={handleEditEmail}
            />
          ))}
        </>
      )}

      <EmailHowItWorksModal
        show={showHowItWorks}
        onHide={() => setShowHowItWorks(false)}
      />

      <ChangeSenderModal
        show={showChangeSender}
        onHide={() => setShowChangeSender(false)}
        senders={senders}
        currentPreferredId={currentPreferredId}
        onConfirm={handleChangeSenderConfirm}
        isLoading={changeSenderStatus === "loading"}
      />

      <CustomToast
        show={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        message={toastMessage}
        variant="success"
        position="bottom-end"
        isMobile={isMobile}
        autohide
        delay={4000}
      />

      <CustomToast
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        message={toastMessage}
        variant="error"
        position="bottom-end"
        isMobile={isMobile}
        autohide
        delay={4000}
      />
    </PageLayout>
  );
};

export default EmailAutomation;
